import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Instagram } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#0A0A0A] border-t border-white/10 py-12" data-id="element-121">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-122">
          <a href="#" className="block bg-black rounded-sm p-1" data-id="element-123">
            <img src="https://cdn.magicpatterns.com/uploads/rBHD8bTXffpvN8g1C1ES7H/PCS_Logo.png" alt="PCS – Websites & Content Studio" className="h-8 w-auto" data-id="element-124" />
          </a>

          <div className="flex items-center gap-6 text-sm text-neutral-500" data-id="element-125">
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors" data-id="element-126">
              Terms & Conditions
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors" data-id="element-127">
              Privacy Policy
            </button>
            <a href="https://www.instagram.com/pinnaclecreativesolutionco" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-id="element-128">
              <Instagram size={16} data-id="element-129" />
              Instagram
            </a>
          </div>

          <div className="text-sm text-neutral-600" data-id="element-130">
            &copy; {new Date().getFullYear()} PCS. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-131">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-132">
            <motion.div initial={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="bg-[#111111] border border-white/10 rounded-sm w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-133">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0" data-id="element-134">
                <h2 className="font-display text-xl font-bold text-white" data-id="element-135">
                  {activeModal === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-neutral-400 hover:text-white transition-colors" data-id="element-136">
                  <X size={20} data-id="element-137" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto text-sm text-neutral-400 leading-relaxed space-y-6" data-id="element-138">
                {activeModal === 'terms' ? <>
                    <p className="text-neutral-300 font-medium" data-id="element-139">
                      Last updated: April 2026
                    </p>

                    <div data-id="element-140">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-141">
                        1. Agreement to Terms
                      </h3>
                      <p data-id="element-142">
                        By accessing or using PCS Websites & Content Studio
                        services, you agree to be bound by these Terms &
                        Conditions. If you do not agree, you may not use our
                        services.
                      </p>
                    </div>

                    <div data-id="element-143">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-144">
                        2. Services
                      </h3>
                      <p data-id="element-145">
                        PCS provides custom web development, content strategy,
                        and digital transformation services. All deliverables,
                        timelines, and scope are defined in individual project
                        agreements signed prior to engagement.
                      </p>
                    </div>

                    <div data-id="element-146">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-147">
                        3. Payment Terms
                      </h3>
                      <p data-id="element-148">
                        All projects require a 50% deposit before work begins.
                        The remaining balance is due upon project completion or
                        as outlined in the project agreement. Late payments may
                        incur a 1.5% monthly fee.
                      </p>
                    </div>

                    <div data-id="element-149">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-150">
                        4. Intellectual Property
                      </h3>
                      <p data-id="element-151">
                        Upon full payment, all custom-developed assets (code,
                        designs, content) are transferred to the client. PCS
                        retains the right to showcase completed work in its
                        portfolio unless otherwise agreed in writing.
                      </p>
                    </div>

                    <div data-id="element-152">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-153">
                        5. 90-Day Performance Guarantee
                      </h3>
                      <p data-id="element-154">
                        Our performance guarantee applies to measurable metrics
                        (site speed, engagement, conversions) as defined in the
                        project agreement. Claims must be submitted in writing
                        within the 90-day window with supporting data.
                      </p>
                    </div>

                    <div data-id="element-155">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-156">
                        6. Limitation of Liability
                      </h3>
                      <p data-id="element-157">
                        PCS shall not be liable for indirect, incidental, or
                        consequential damages. Our total liability shall not
                        exceed the amount paid by the client for the specific
                        service in question.
                      </p>
                    </div>

                    <div data-id="element-158">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-159">
                        7. Termination
                      </h3>
                      <p data-id="element-160">
                        Either party may terminate the engagement with 30 days
                        written notice. Work completed up to the termination
                        date will be billed proportionally. Deposits are
                        non-refundable.
                      </p>
                    </div>

                    <div data-id="element-161">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-162">
                        8. Governing Law
                      </h3>
                      <p data-id="element-163">
                        These terms are governed by applicable law. Any disputes
                        shall be resolved through binding arbitration.
                      </p>
                    </div>
                  </> : <>
                    <p className="text-neutral-300 font-medium" data-id="element-164">
                      Last updated: April 2026
                    </p>

                    <div data-id="element-165">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-166">
                        1. Information We Collect
                      </h3>
                      <p data-id="element-167">
                        We collect information you provide directly, such as
                        your name, company, and project details when you engage
                        our services or submit inquiries through our website.
                      </p>
                    </div>

                    <div data-id="element-168">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-169">
                        2. How We Use Your Information
                      </h3>
                      <p data-id="element-170">
                        Your information is used solely to deliver our services,
                        communicate about your project, and improve our
                        offerings. We do not sell, rent, or share your personal
                        data with third parties for marketing purposes.
                      </p>
                    </div>

                    <div data-id="element-171">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-172">
                        3. Data Security
                      </h3>
                      <p data-id="element-173">
                        We implement industry-standard security measures to
                        protect your data. All project files and communications
                        are encrypted in transit and at rest.
                      </p>
                    </div>

                    <div data-id="element-174">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-175">
                        4. Cookies & Analytics
                      </h3>
                      <p data-id="element-176">
                        Our website uses essential cookies and
                        privacy-respecting analytics to understand site
                        performance. No personal data is shared with advertising
                        networks.
                      </p>
                    </div>

                    <div data-id="element-177">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-178">
                        5. Your Rights
                      </h3>
                      <p data-id="element-179">
                        You have the right to access, correct, or delete your
                        personal data at any time. You may also request a copy
                        of all data we hold about you.
                      </p>
                    </div>

                    <div data-id="element-180">
                      <h3 className="text-white font-display font-semibold mb-2" data-id="element-181">
                        6. Data Retention
                      </h3>
                      <p data-id="element-182">
                        We retain project-related data for 24 months after
                        project completion. After this period, data is securely
                        deleted unless retention is required by law.
                      </p>
                    </div>
                  </>}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-white/10 shrink-0" data-id="element-183">
                <button onClick={() => setActiveModal(null)} className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white py-3 rounded-sm font-medium transition-colors text-sm" data-id="element-184">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}